import styled from "styled-components";
import { motion } from "framer-motion";
import {
  Brain,
  Code,
  ArrowRight,
  Sparkles,
  Target,
  Briefcase,
  Trophy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

/* ================= PAGE ================= */

const Page = styled.section`
  min-height: 100vh;
  padding: 140px 20px 100px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
`;

const HeroShell = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 22px;
  align-items: center;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

/* ================= TEXT ================= */

const Heading = styled(motion.h1)`
  margin: 16px 0 0;
  font-size: clamp(42px, 5vw, 60px);
  font-weight: 900;
  line-height: 1.08;
  color: #f8fafc;
`;

const Sub = styled(motion.p)`
  margin: 18px 0 0;
  max-width: 760px;
  font-size: 18px;
  line-height: 1.8;
  color: #94a3b8;
`;

const Soft = styled.span`
  color: #e2e8f0;
  font-weight: 700;
`;

const ProofPanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const ProofLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ProofTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const ProofList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const ProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

/* ================= QUESTION ================= */

const BigQuestion = styled(motion.div)`
  margin: 28px auto 0;
  padding: 28px;
  border-radius: 28px;
  text-align: left;
  background: rgba(2, 6, 23, 0.52);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const QuestionText = styled.div`
  font-size: clamp(28px, 4.2vw, 40px);
  font-weight: 900;
  color: #f8fafc;
`;

const SkipChip = styled.button`
  margin-top: 24px;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 15px;
  background: transparent;
  border: 1px dashed rgba(56, 189, 248, 0.35);
  color: #cbd5e1;
  cursor: pointer;

  &:hover {
    border-color: rgba(56, 189, 248, 0.6);
    color: #f8fafc;
  }
`;

/* ================= PATHS ================= */

const PathGrid = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 48px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PathCard = styled(motion.div)`
  padding: 52px;
  border-radius: 36px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.74)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);

  transition: all 0.35s ease;
`;

const PathIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 22px;
  display: grid;
  place-items: center;
  background: ${({ $tone }) =>
    $tone === "dsa"
      ? "linear-gradient(135deg, #38bdf8, #0ea5e9)"
      : "linear-gradient(135deg, #f59e0b, #d97706)"};
  color: white;
  margin-bottom: 28px;
`;

const PathBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "dsa"
      ? "rgba(56, 189, 248, 0.12)"
      : "rgba(245, 158, 11, 0.12)"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "dsa"
        ? "rgba(56, 189, 248, 0.2)"
        : "rgba(245, 158, 11, 0.2)"};
  color: ${({ $tone }) => ($tone === "dsa" ? "#7dd3fc" : "#fcd34d")};
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const PathTitle = styled.h3`
  margin-top: 16px;
  font-size: 28px;
  font-weight: 900;
  margin-bottom: 20px;
  color: #f8fafc;
`;

const List = styled.ul`
  padding-left: 18px;
  margin-bottom: 32px;

  li {
    margin-bottom: 12px;
    font-size: 16px;
    line-height: 1.7;
    color: #94a3b8;
  }
`;

const CardSub = styled.p`
  margin: 0 0 20px;
  color: #cbd5e1;
  font-size: 15px;
  line-height: 1.7;
`;

const CTA = styled.button`
  padding: 14px 24px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 800;
  font-size: 16px;
  background: ${({ $tone }) =>
    $tone === "dsa"
      ? "linear-gradient(90deg, #38bdf8, #0ea5e9)"
      : "linear-gradient(90deg, #f59e0b, #d97706)"};
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ $tone }) =>
      $tone === "dsa"
        ? "0 12px 36px rgba(56, 189, 248, 0.32)"
        : "0 12px 36px rgba(245, 158, 11, 0.28)"};
  }
`;
/* ================= MODAL ================= */

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.65);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: grid;
  place-items: center;
`;

const Modal = styled(motion.div)`
  width: min(92%, 700px);
  padding: 40px;
  border-radius: 32px;

  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.92), rgba(2, 6, 23, 0.88)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.08), transparent 34%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 40px 120px rgba(2, 6, 23, 0.7);
`;

const ModalTitle = styled.h3`
  font-size: 28px;
  font-weight: 900;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.colors.text};
`;

const RoleBlock = styled.div`
  margin-bottom: 22px;
`;

const RoleTitle = styled.div`
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.colors.text};
`;

const RoleDesc = styled.div`
  font-size: 15px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CloseBtn = styled.button`
  margin-top: 28px;
  padding: 12px 20px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.15);
  color: ${({ theme }) => theme.colors.text};

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const SecondaryCTA = styled.button`
  margin-top: 18px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 700;
  color: #cbd5e1;
  cursor: pointer;

  &:hover {
    color: #f8fafc;
  }
`;

/* ================= ANIMATION ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

/* ================= COMPONENT ================= */

export default function ChoosePathPage() {
  const navigate = useNavigate();
  const [showRoles, setShowRoles] = useState(false);

  return (
    <Page>
      <Container>
        <HeroShell>
          <HeroGrid>
            <div>
              <Eyebrow>
                <Sparkles size={14} />
                Choose Your Starting Direction
              </Eyebrow>
              <Heading variants={fadeUp} initial="hidden" animate="show">
                Start your developer journey with the right path
              </Heading>

              <Sub variants={fadeUp} initial="hidden" animate="show">
                You don’t need to plan your full career today. Just choose how
                you want to <Soft>prepare for your first real tech job</Soft>,
                and we’ll guide the next steps from there.
              </Sub>
            </div>

            <ProofPanel>
              <ProofLabel>What happens next</ProofLabel>
              <ProofTitle>One choice. Clearer execution.</ProofTitle>
              <ProofList>
                <ProofItem>
                  <Briefcase size={16} />
                  Pick job-ready engineering or DSA-first preparation
                </ProofItem>
                <ProofItem>
                  <Target size={16} />
                  Move into a direction that matches how you want to prepare
                </ProofItem>
                <ProofItem>
                  <Trophy size={16} />
                  Reduce confusion before starting the clarity flow
                </ProofItem>
              </ProofList>
            </ProofPanel>
          </HeroGrid>

          <BigQuestion variants={fadeUp} initial="hidden" animate="show">
            <QuestionText>How do you want to prepare for a job?</QuestionText>

            <SkipChip onClick={() => navigate("/career-clarity")}>
              I’m confused, help me decide →
            </SkipChip>
          </BigQuestion>
        </HeroShell>

        {/* PATH OPTIONS */}
        <PathGrid>
          {/* ROLE BASED */}
          <PathCard whileHover={{ y: -12 }}>
            <PathBadge $tone="role">
              <Briefcase size={14} />
              Role-based path
            </PathBadge>

            <PathTitle>Become a job-ready role-based engineer</PathTitle>
            <CardSub>
              Best for learners who want a roadmap toward real frontend,
              backend, or mobile developer roles with project-based execution.
            </CardSub>

            <PathIcon $tone="role">
              <Code size={32} />
            </PathIcon>

            <List>
              <li>Prepare for a specific developer job role</li>
              <li>Frontend, Mobile or Backend Engineer</li>
              <li>Learn skills expected in real company roles</li>
              <li>Build job-oriented, role-specific projects</li>
              <li>Apply confidently for developer openings</li>
            </List>

            <CTA
              $tone="role"
              onClick={() =>
                navigate("/career-clarity", {
                  state: { path: "product" },
                })
              }
            >
              Get job-ready → <ArrowRight size={16} />
            </CTA>

            <SecondaryCTA onClick={() => setShowRoles(true)}>
              View roles & responsibilities →
            </SecondaryCTA>
          </PathCard>

          {/* DSA */}
          <PathCard whileHover={{ y: -12 }}>
            <PathBadge $tone="dsa">
              <Brain size={14} />
              DSA-focused path
            </PathBadge>

            <PathTitle>DSA & Interview Prep</PathTitle>
            <CardSub>
              Best for learners who already know their direction and want to
              focus deeply on problem solving, coding rounds, and interview
              performance.
            </CardSub>

            <PathIcon $tone="dsa">
              <Brain size={32} />
            </PathIcon>

            <List>
              <li>Focus on problem solving</li>
              <li>Data Structures & Algorithms</li>
              <li>Prepare for coding interviews</li>
              <li>Used in top product companies</li>
              <li>Needs patience & consistency</li>
            </List>

            <CTA
              $tone="dsa"
              onClick={() =>
                navigate("/career-clarity", {
                  state: { path: "dsa" },
                })
              }
            >
              Continue → <ArrowRight size={16} />
            </CTA>
          </PathCard>
        </PathGrid>
      </Container>
      {showRoles && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowRoles(false)}
        >
          <Modal
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalTitle>Developer Roles & What You’ll Do</ModalTitle>

            <RoleBlock>
              <RoleTitle>Frontend Developer</RoleTitle>
              <RoleDesc>
                Build user interfaces and web applications.
                <br />
                Work with designs, APIs, and performance.
                <br />
                Focus on React, UI logic, and user experience.
              </RoleDesc>
            </RoleBlock>

            <RoleBlock>
              <RoleTitle>Mobile Developer</RoleTitle>
              <RoleDesc>
                Build Android & iOS apps.
                <br />
                Handle app navigation, performance, and device features.
                <br />
                Focus on React Native and mobile UX.
              </RoleDesc>
            </RoleBlock>

            <RoleBlock>
              <RoleTitle>Backend Developer</RoleTitle>
              <RoleDesc>
                Build APIs, databases, and business logic.
                <br />
                Handle authentication, data, and scalability.
                <br />
                Focus on servers, databases, and system reliability.
              </RoleDesc>
            </RoleBlock>

            <CloseBtn onClick={() => setShowRoles(false)}>Close</CloseBtn>
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
